import React, { useState, useEffect } from 'react';
import { useGameStore } from '../store/gameStore';

interface PowerUpCardProps {
  title: string;
  description: string;
  icon: string;
  cost: number;
  duration: number;
  onPurchase: () => void;
  canAfford: boolean;
  isActive: boolean;
  category: 'basic' | 'advanced' | 'premium';
  timeLeft?: number; // Neue Prop für verbleibende Zeit
}

const PowerUpCard: React.FC<PowerUpCardProps> = ({
  title,
  description,
  icon,
  cost,
  duration,
  onPurchase,
  canAfford,
  isActive,
  category,
  timeLeft = 0
}) => {
  const { formatNumber } = useGameStore();
  
  const getCategoryColor = () => {
    switch (category) {
      case 'basic': return 'from-green-400 to-green-600';
      case 'advanced': return 'from-blue-400 to-blue-600';
      case 'premium': return 'from-purple-400 to-purple-600';
      default: return 'from-gray-400 to-gray-600';
    }
  };

  const getCategoryBorder = () => {
    switch (category) {
      case 'basic': return 'border-green-300';
      case 'advanced': return 'border-blue-300';
      case 'premium': return 'border-purple-300';
      default: return 'border-gray-300';
    }
  };

  // Berechne Fortschritt für die Leiste (0-100%)
  const progressPercentage = isActive && duration > 0 ? ((duration - timeLeft) / duration) * 100 : 0;
  
  // Formatiere die verbleibende Zeit
  const formatTimeLeft = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Bestimme die Farbe der Fortschrittsleiste basierend auf verbleibender Zeit
  const getProgressBarColor = () => {
    if (!isActive) return 'bg-gray-200';
    const timePercentage = (timeLeft / duration) * 100;
    if (timePercentage > 60) return 'bg-green-500';
    if (timePercentage > 30) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  return (
    <div className={`relative p-2 sm:p-3 rounded-lg border ${getCategoryBorder()} ${
      isActive ? 'bg-yellow-50 shadow-lg' : 'bg-white'
    } transition-all duration-200 hover:shadow-md`}>
      {isActive && (
        <div className="absolute -top-1 -right-1 bg-yellow-400 text-yellow-800 px-1.5 py-0.5 rounded-full text-xs font-bold">
          AKTIV
        </div>
      )}
      
      <div className="flex items-center mb-2">
        <div className="text-xl sm:text-2xl mr-2">{icon}</div>
        <div className="flex-1">
          <h3 className="font-bold text-sm sm:text-base">{title}</h3>
          <p className="text-xs text-gray-600 leading-tight">{description}</p>
        </div>
      </div>
      
      {/* Fortschrittsleiste für aktive Power-Ups */}
      {isActive && (
        <div className="mb-2">
          <div className="flex justify-between items-center mb-1">
            <span className="text-xs text-gray-600">Zeit:</span>
            <span className="text-xs font-bold text-gray-800">{formatTimeLeft(timeLeft)}</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-1.5 overflow-hidden">
            <div 
              className={`h-full rounded-full transition-all duration-1000 ease-out ${getProgressBarColor()}`}
              style={{ width: `${100 - progressPercentage}%` }}
            />
          </div>
        </div>
      )}
      
      <div className="flex justify-between items-center mb-2 text-xs">
        <span className="font-medium">⏱️ {duration}s</span>
        <div className="flex items-center">
          <span className="text-sm font-bold text-blue-600">{formatNumber(cost)}</span>
          <span className="text-blue-600 ml-1">💎</span>
        </div>
      </div>
      
      <button
        onClick={onPurchase}
        disabled={!canAfford || isActive}
        className={`w-full py-1.5 sm:py-2 px-3 rounded-lg font-semibold text-xs sm:text-sm transition-all duration-200 ${
          isActive
            ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
            : canAfford
            ? `bg-gradient-to-r ${getCategoryColor()} text-white hover:shadow-lg transform hover:scale-105`
            : 'bg-gray-200 text-gray-400 cursor-not-allowed'
        }`}
      >
        {isActive ? 'Aktiv' : canAfford ? 'Kaufen' : 'Nicht genug 💎'}
      </button>
    </div>
  );
};

export const PowerUpShop: React.FC = () => {
  const { gems, powerUps, activatePowerUp, spendGems, formatNumber, updatePowerUps } = useGameStore();
  
  // State für das erzwungene Re-Rendering jede Sekunde
  const [, forceUpdate] = useState({});

  // Aktualisiere Power-Ups und zwinge Re-Rendering jede Sekunde
  useEffect(() => {
    const interval = setInterval(() => {
      updatePowerUps();
      forceUpdate({}); // Erzwinge Re-Rendering für die Fortschrittsleiste
    }, 1000);

    return () => clearInterval(interval);
  }, [updatePowerUps]);

  const powerUpCategories = {
    basic: [
      {
        key: 'goldRush' as const,
        title: 'Gold Rush',
        description: 'Verdoppelt alle Münz-Einkommen für eine Minute',
        icon: '💰',
        cost: 500,
        duration: 60,
      },
      {
        key: 'speedBoost' as const,
        title: 'Speed Boost',
        description: 'Verdoppelt die Geschwindigkeit aller Auto-Clicks',
        icon: '⚡',
        cost: 300,
        duration: 45,
      },
      {
        key: 'luckyCharm' as const,
        title: 'Lucky Charm',
        description: '10% Chance auf zusätzliche Edelsteine bei Klicks',
        icon: '🍀',
        cost: 400,
        duration: 90,
      },
    ],
    advanced: [
      {
        key: 'megaMultiplier' as const,
        title: 'Mega-Multiplikator',
        description: 'Verfünffacht alle Klick-Multiplier für 30 Sekunden',
        icon: '🚀',
        cost: 800,
        duration: 30,
      },
      {
        key: 'gemStorm' as const,
        title: 'Edelstein-Sturm',
        description: 'Jeder Klick generiert garantiert Edelsteine',
        icon: '💎',
        cost: 1200,
        duration: 20,
      },
      {
        key: 'timeWarp' as const,
        title: 'Zeit-Verzerrung',
        description: 'Alles läuft 10x schneller - ultimative Beschleunigung',
        icon: '⏰',
        cost: 1500,
        duration: 15,
      },
      {
        key: 'gardenBlessing' as const,
        title: 'Garten-Segen',
        description: 'Verdreifacht alle Garten-Bereiche Boni',
        icon: '🌿',
        cost: 1000,
        duration: 45,
      },
      {
        key: 'clickFrenzy' as const,
        title: 'Klick-Raserei',
        description: 'Auto-Klicks werden zu Mega-Klicks (5x Stärke)',
        icon: '🔥',
        cost: 700,
        duration: 25,
      },
      {
        key: 'diamondMidas' as const,
        title: 'Diamant-Midas',
        description: '0.1% aller Münzen werden zu Edelsteinen',
        icon: '✨',
        cost: 2000,
        duration: 30,
      },
      {
        key: 'infiniteEnergy' as const,
        title: 'Unendliche Energie',
        description: 'Klicks kosten keine Energie und wirken doppelt',
        icon: '🌟',
        cost: 1800,
        duration: 40,
      },
    ],
    premium: [
      {
        key: 'cosmicBoost' as const,
        title: 'Kosmischer Boost',
        description: 'ALLE Boni werden x10 verstärkt - göttliche Macht!',
        icon: '🌌',
        cost: 5000,
        duration: 20,
      },
      {
        key: 'dragonPower' as const,
        title: 'Drachen-Macht',
        description: 'Legendärer Drachen-Boost - x25 ALLES! Ultimative Macht!',
        icon: '🐉',
        cost: 10000,
        duration: 10,
      },
      {
        key: 'experienceRush' as const,
        title: 'EP-Rausch',
        description: '100x EP-Bonus für 10 Sekunden - maximale Erfahrung!',
        icon: '⭐',
        cost: 10000,
        duration: 10,
      },
    ],
  };

  // Berechne die Kosten für Power-Ups basierend auf der Kategorie
  const calculatePowerUpCost = (category: 'basic' | 'advanced' | 'premium', baseCost: number, powerUpKey?: string) => {
    if (category === 'basic') {
      return baseCost; // Basis Power-Ups behalten ihre ursprünglichen Kosten
    } else {
      // Spezielle Kosten für Mega Multiplikator, Edelstein Sturm und alle Premium Power-Ups
      if (powerUpKey === 'megaMultiplier' || powerUpKey === 'gemStorm' || category === 'premium') {
        const costPercentage = Math.floor(gems * 0.1);
        return Math.max(1000000, costPercentage);
      } else {
        // Andere erweiterte Power-Ups kosten 10% der Diamanten, mindestens 10.000
        const costPercentage = Math.floor(gems * 0.1);
        return Math.max(10000, costPercentage);
      }
    }
  };

  const handlePurchase = (powerUpKey: keyof typeof powerUps, baseCost: number, duration: number, category: 'basic' | 'advanced' | 'premium') => {
    const actualCost = calculatePowerUpCost(category, baseCost);
    if (gems >= actualCost && !powerUps[powerUpKey].isActive) {
      spendGems(actualCost);
      activatePowerUp(powerUpKey, duration);
    }
  };

  return (
    <div className="max-w-7xl mx-auto p-3 sm:p-6">
      {/* Basis Power-Ups */}
      <div className="mb-4 sm:mb-6">
        <h2 className="text-base sm:text-xl font-bold text-green-700 mb-2 sm:mb-3 text-center">
          🌱 Basis Power-Ups
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-2 sm:gap-4">
          {powerUpCategories.basic.map((config) => (
            <PowerUpCard
              key={config.key}
              title={config.title}
              description={config.description}
              icon={config.icon}
              cost={calculatePowerUpCost("basic", config.cost, config.key)}
              duration={config.duration}
              canAfford={gems >= calculatePowerUpCost("basic", config.cost, config.key)}
              isActive={powerUps[config.key].isActive}
              timeLeft={powerUps[config.key].timeLeft}
              category="basic"
              onPurchase={() => handlePurchase(config.key, config.cost, config.duration, "basic")}
            />
          ))}
        </div>
      </div>

      {/* Erweiterte Power-Ups */}
      <div className="mb-4 sm:mb-6">
        <h2 className="text-base sm:text-xl font-bold text-blue-700 mb-2 sm:mb-3 text-center">
          ⚡ Erweiterte Power-Ups
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-2 sm:gap-4">
          {powerUpCategories.advanced.map((config) => (
            <PowerUpCard
              key={config.key}
              title={config.title}
              description={config.description}
              icon={config.icon}
              cost={calculatePowerUpCost("advanced", config.cost, config.key)}
              duration={config.duration}
              canAfford={gems >= calculatePowerUpCost("advanced", config.cost, config.key)}
              isActive={powerUps[config.key].isActive}
              timeLeft={powerUps[config.key].timeLeft}
              category="advanced"
              onPurchase={() => handlePurchase(config.key, config.cost, config.duration, "advanced")}
            />
          ))}
        </div>
      </div>

      {/* Premium Power-Ups */}
      <div className="mb-4 sm:mb-6">
        <h2 className="text-base sm:text-xl font-bold text-purple-700 mb-2 sm:mb-3 text-center">
          👑 Premium Power-Ups
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-4">
          {powerUpCategories.premium.map((config) => (
            <PowerUpCard
              key={config.key}
              title={config.title}
              description={config.description}
              icon={config.icon}
              cost={calculatePowerUpCost("premium", config.cost, config.key)}
              duration={config.duration}
              canAfford={gems >= calculatePowerUpCost("premium", config.cost, config.key)}
              isActive={powerUps[config.key].isActive}
              timeLeft={powerUps[config.key].timeLeft}
              category="premium"
              onPurchase={() => handlePurchase(config.key, config.cost, config.duration, "premium")}
            />
          ))}
        </div>
      </div>
    </div>
  );
}; 